/**
 * Merge frontend menu catalog with backend workspace menu_settings
 *
 * 머지 규칙 (백엔드 안내서 기반):
 * - 카탈로그 ∩ settings: settings의 `is_active`/`sort_order` 사용
 * - 카탈로그에만 있음: `is_active=defaultActive`, `sort_order=∞` (맨 뒤로)
 * - settings에만 있음: 무시 (카탈로그가 화이트리스트)
 * - adminOnly 항목: hasAdminPrivileges=false면 숨김 (백엔드 활성 여부와 무관)
 * - 1depth 비활성: 1depth 자체를 표시하지 않음 (children은 자연스럽게 함께 사라짐)
 * - 정렬: 1depth → 같은 parent 안에서 `sort_order` → tiebreak로 `menu_key`
 */

import type { ReactNode } from 'react'
import type { WorkspaceMenuItemSchema } from '@/api/generated/models/WorkspaceMenuItemSchema'
import {
  MENU_CATALOG,
  MENU_CATALOG_MAP,
  type MenuCatalogEntry,
  type MenuKey,
  type ParentMenuKey,
} from './menuCatalog'

/** Sidebar 렌더에서 사용하는 항목 형태 (Sidebar/index.tsx의 MenuItem 인터페이스와 일치) */
export interface SidebarMenuItem {
  id: string
  label: string
  icon?: ReactNode
  children?: SidebarMenuItem[]
  isActive?: boolean
  onClick?: () => void
  path?: string
}

/** 머지 결과의 중간 표현 — Sidebar 렌더 직전 단계에서 icon/onClick을 채워 SidebarMenuItem이 됨 */
export interface ResolvedMenuEntry {
  catalog: MenuCatalogEntry
  isActive: boolean
  sortOrder: number
}

/**
 * 카탈로그 + settings → 정렬된 ResolvedMenuEntry 트리.
 *
 * Sidebar에서 icon/onClick을 추가로 채워 SidebarMenuItem으로 변환한다.
 * EditPanel에서는 이 ResolvedMenuEntry 형태가 그대로 편집 state로 쓰인다.
 */
export function resolveMenuTree(
  settings: WorkspaceMenuItemSchema[] | null | undefined,
  hasAdminPrivileges: boolean,
): Array<{ parent: ResolvedMenuEntry; children: ResolvedMenuEntry[] }> {
  const settingsMap = new Map<string, WorkspaceMenuItemSchema>()
  if (settings) {
    for (const s of settings) {
      if (MENU_CATALOG_MAP[s.menu_key]) {
        // 카탈로그에 있는 키만 의미가 있음
        settingsMap.set(s.menu_key, s)
      }
    }
  }

  /** catalog entry → ResolvedMenuEntry 변환 (admin 가드 + 머지 적용) */
  const resolve = (entry: MenuCatalogEntry): ResolvedMenuEntry | null => {
    if (entry.adminOnly && !hasAdminPrivileges) return null

    const s = settingsMap.get(entry.key)
    return {
      catalog: entry,
      isActive: s?.is_active ?? entry.defaultActive,
      // 카탈로그에만 있는 항목은 맨 뒤로 (안내서 권장).
      // tiebreak를 위해 카탈로그 defaultSortOrder를 보존하는 큰 base를 더한다.
      sortOrder: s?.sort_order ?? 1_000_000 + entry.defaultSortOrder,
    }
  }

  // 1depth 정렬
  const parents = MENU_CATALOG.filter(e => e.parentKey === null)
    .map(resolve)
    .filter((e): e is ResolvedMenuEntry => e !== null)
    .sort(compareEntries)

  // 각 parent의 children 정렬
  return parents.map(parent => {
    const parentKey = parent.catalog.key as ParentMenuKey
    const children = MENU_CATALOG.filter(e => e.parentKey === parentKey)
      .map(resolve)
      .filter((e): e is ResolvedMenuEntry => e !== null)
      .sort(compareEntries)
    return { parent, children }
  })
}

function compareEntries(a: ResolvedMenuEntry, b: ResolvedMenuEntry): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
  // tiebreak: 카탈로그 키 알파벳순 (안내서: "sort_order → menu_key")
  return a.catalog.key.localeCompare(b.catalog.key)
}

/**
 * Sidebar 렌더용으로 머지된 메뉴 트리를 SidebarMenuItem[]로 변환한다.
 *
 * @param settings - 백엔드에서 받은 menu_settings (없거나 null이면 카탈로그 기본값으로)
 * @param hasAdminPrivileges - 현재 사용자의 관리자 권한 여부
 * @param iconResolver - 1depth iconName → ReactNode 변환기 (Sidebar에서 lucide 컴포넌트 매핑)
 * @param onNavigate - 2depth 클릭 시 호출 (path 전달)
 * @param isPathActive - 현재 경로와 매칭 여부 판단
 * @returns 비활성/비표시 항목이 모두 제거된 SidebarMenuItem[]
 */
export function mergeMenuSettings(
  settings: WorkspaceMenuItemSchema[] | null | undefined,
  hasAdminPrivileges: boolean,
  iconResolver: (iconName: NonNullable<MenuCatalogEntry['iconName']>) => ReactNode,
  onNavigate: (path: string) => void,
  isPathActive: (path: string) => boolean,
): SidebarMenuItem[] {
  const tree = resolveMenuTree(settings, hasAdminPrivileges)

  return tree
    .filter(({ parent, children }) => {
      // 1depth가 비활성이면 통째로 숨김
      if (!parent.isActive) return false
      // 활성 children이 없는 1depth는 의미가 없으므로 숨김 (기존 동작 유지)
      const visibleChildren = children.filter(c => c.isActive)
      return visibleChildren.length > 0
    })
    .map(({ parent, children }) => ({
      id: parent.catalog.key,
      label: parent.catalog.label,
      icon: parent.catalog.iconName ? iconResolver(parent.catalog.iconName) : undefined,
      isActive: false,
      children: children
        .filter(c => c.isActive)
        .map(c => ({
          id: c.catalog.key,
          label: c.catalog.label,
          path: c.catalog.path,
          isActive: c.catalog.path ? isPathActive(c.catalog.path) : false,
          onClick: c.catalog.path ? () => onNavigate(c.catalog.path!) : undefined,
        })),
    }))
}

/**
 * EditPanel에서 편집한 ResolvedMenuEntry 트리를 백엔드 PUT 페이로드로 변환한다.
 *
 * sort_order는 배열 인덱스로 새로 부여 (드래그 결과를 그대로 반영).
 */
export function buildMenuSettingsPayload(
  tree: Array<{ parent: ResolvedMenuEntry; children: ResolvedMenuEntry[] }>,
): WorkspaceMenuItemSchema[] {
  const items: WorkspaceMenuItemSchema[] = []
  tree.forEach(({ parent, children }, parentIdx) => {
    items.push({
      menu_key: parent.catalog.key,
      parent_key: null,
      is_active: parent.isActive,
      sort_order: parentIdx,
    })
    children.forEach((child, childIdx) => {
      items.push({
        menu_key: child.catalog.key,
        parent_key: parent.catalog.key,
        is_active: child.isActive,
        sort_order: childIdx,
      })
    })
  })
  return items
}

/** Re-export for convenience */
export type { MenuKey, ParentMenuKey, MenuCatalogEntry }
